/**
 * Service HTTP de Profiles — CRUD tenant-escopado.
 *
 * Endpoints cobertos (Orchestrator API §User and Profile Endpoints):
 *   GET    /api/v1/profiles
 *   POST   /api/v1/profiles
 *   PATCH  /api/v1/profiles/{id}
 *   DELETE /api/v1/profiles/{id}
 *
 * Header obrigatório: `X-Tenant-ID`. Flag exigida: `can_manage_users`.
 *
 * Particularidades:
 *  - Perfis padrão (`tenant_admin`, `engineer`, `viewer`) têm
 *    `is_system_default = true` e **não podem ser deletados** (backend
 *    retorna 400). Podem ser editados normalmente.
 *  - Ao criar, o servidor força `is_system_default = false` mesmo que o
 *    cliente envie outro valor.
 *  - O POST exige **todas as 9 flags** explicitamente.
 *
 * Spec frontend §11.13 — tabela + Sheet com matrix de checkboxes.
 */

import { api } from '@/services/api'
import type { Profile } from '@/types/api'

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

const BASE = '/api/v1/profiles'

const paths = {
  list: BASE,
  create: BASE,
  detail: (id: string) => `${BASE}/${id}`,
} as const

// ---------------------------------------------------------------------------
// Request payloads
// ---------------------------------------------------------------------------

/**
 * Body do `POST /profiles`. Todas as 9 flags são obrigatórias (sem default).
 * `is_system_default` é forçado pelo servidor como `false` — não enviar.
 */
export interface CreateProfileInput {
  name: string
  can_create_test: boolean
  can_view_tests: boolean
  can_edit_test: boolean
  can_delete_test: boolean
  can_execute_test: boolean
  can_manage_schedules: boolean
  can_view_reports: boolean
  can_manage_users: boolean
  can_view_billing: boolean
  can_approve_curation?: boolean
}

/**
 * Body do `PATCH /profiles/{id}`. Permite editar `name`, qualquer flag e
 * `is_active` — inclusive em perfis padrão.
 */
export interface UpdateProfileInput {
  name?: string
  can_create_test?: boolean
  can_view_tests?: boolean
  can_edit_test?: boolean
  can_delete_test?: boolean
  can_execute_test?: boolean
  can_manage_schedules?: boolean
  can_view_reports?: boolean
  can_manage_users?: boolean
  can_view_billing?: boolean
  can_approve_curation?: boolean
  is_active?: boolean
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const profilesService = {
  /** Lista perfis (padrão + customizados) do tenant ativo. */
  async list(): Promise<Profile[]> {
    const { data } = await api.get<Profile[]>(paths.list)
    return data
  },

  /** Cria perfil customizado. `is_system_default` é forçado a `false`. */
  async create(input: CreateProfileInput): Promise<Profile> {
    const { data } = await api.post<Profile>(paths.create, input)
    return data
  },

  /** Atualiza nome, flags ou `is_active` de um perfil (parcial). */
  async update(id: string, input: UpdateProfileInput): Promise<Profile> {
    const { data } = await api.patch<Profile>(paths.detail(id), input)
    return data
  },

  /**
   * Soft delete. **400** se o perfil for `is_system_default = true` —
   * a UI deve esconder/desabilitar o botão "Deletar" nesses casos.
   */
  async delete(id: string): Promise<void> {
    await api.delete<void>(paths.detail(id))
  },
}

export type ProfilesService = typeof profilesService
